import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { playerApi } from '../../utils/api';
import { supabase } from '../../lib/supabase';
import { useTeamStore } from '../../store/teamStore';
import { INDIVIDUAL_SPORTS, FORMAT_BY_SPORT, getEventLabel } from '../../data/sportRulesets';
import type { Player, Sport, Team } from '../../types';

interface IndividualProfileSettingsSectionProps {
  team: Team;
}

/**
 * Name + sport editing for a personal (parent/player) account — the two
 * things Team Information normally covers that still apply here. Everything
 * else in that section (roster size, scheme, age level) is a coaching
 * concern this account never sees.
 *
 * Auto-saves like the rest of the Settings page (the banner above says so) —
 * name changes debounce 1s after typing stops, matching useSettings' own
 * debounce; sport changes save immediately since a <select> has no "typing."
 */
export const IndividualProfileSettingsSection: React.FC<IndividualProfileSettingsSectionProps> = ({ team }) => {
  const { updateTeam } = useTeamStore();
  const [player, setPlayer] = useState<Player | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [sport, setSport] = useState<Sport>(team.sport);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const isInitialLoadRef = useRef(true);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      isInitialLoadRef.current = true;
      const result = await playerApi.getByTeam(team.id);
      if (cancelled) return;
      const p = result.success && result.data && result.data.length > 0 ? result.data[0] : null;
      setPlayer(p);
      if (p) {
        setFirstName(p.first_name);
        setLastName(p.last_name);
      }
      setSport(team.sport);
      setLoading(false);
      // Let the state above settle before the name-save effect is allowed to fire.
      setTimeout(() => {
        if (!cancelled) isInitialLoadRef.current = false;
      }, 0);
    })();
    return () => {
      cancelled = true;
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [team.id, team.sport]);

  const saveName = async () => {
    if (!player || !firstName.trim() || !lastName.trim()) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('players')
        .update({ first_name: firstName.trim(), last_name: lastName.trim() })
        .eq('id', player.id);
      if (error) throw error;
      await updateTeam({ name: `${firstName.trim()} ${lastName.trim()}` });
    } catch (error) {
      console.error('[IndividualProfileSettingsSection] Error saving name:', error);
      toast.error('Failed to update name');
    } finally {
      setSaving(false);
    }
  };

  // Debounced auto-save for name fields, 1s after typing stops (matches useSettings' own debounce).
  useEffect(() => {
    if (loading || isInitialLoadRef.current) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      void saveName();
    }, 1000);
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstName, lastName]);

  const handleSportChange = async (nextSport: Sport) => {
    setSport(nextSport);
    setSaving(true);
    try {
      await updateTeam({ sport: nextSport, format: FORMAT_BY_SPORT[nextSport] });
    } catch (error) {
      console.error('[IndividualProfileSettingsSection] Error saving sport:', error);
      toast.error('Failed to update sport');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="h-24 flex items-center justify-center text-sm text-gray-500">Loading...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="individual-first-name" className="form-label">First name</label>
          <input
            id="individual-first-name"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
        <div>
          <label htmlFor="individual-last-name" className="form-label">Last name</label>
          <input
            id="individual-last-name"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      </div>

      <div>
        <label htmlFor="individual-sport" className="form-label">Sport</label>
        <select
          id="individual-sport"
          value={sport}
          onChange={(e) => handleSportChange(e.target.value as Sport)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
        >
          {INDIVIDUAL_SPORTS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Changing your sport won't affect {getEventLabel(team.sport).plural.toLowerCase()} you've already logged.
        </p>
      </div>

      {saving && <p className="text-xs text-gray-500">Saving...</p>}
    </div>
  );
};

export default IndividualProfileSettingsSection;
